// Servizio per riconoscimento vocale usando Web Speech API
// Supporta multilingua e riconoscimento automatico della lingua

class SpeechRecognitionService {
  constructor() {
    this.recognition = null;
    this.isSupported = false;
    this.isListening = false;
    this.onResultCallback = null;
    this.onErrorCallback = null;
    this.onEndCallback = null;
    this.maxDuration = 30000; // 30 secondi in millisecondi (allineato con MAX_RECORDING_TIME)
    this.timer = null;
    
    this.init();
  }

  init() {
    // Verifica supporto Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Web Speech API non supportata in questo browser');
      this.isSupported = false;
      return;
    }

    this.isSupported = true;
    this.recognition = new SpeechRecognition();
    
    // Rileva lingua del browser
    const browserLang = navigator.language || navigator.userLanguage || 'en-US';
    const langCode = browserLang.split('-')[0]; // Prende solo il codice lingua base (es: 'it' da 'it-IT')
    
    // Mappa codici lingua comuni a codici Speech Recognition
    const langMap = {
      'it': 'it-IT',
      'en': 'en-US',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'de': 'de-DE',
      'pt': 'pt-BR',
      'ru': 'ru-RU',
      'zh': 'zh-CN',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
      'ar': 'ar-SA',
      'nl': 'nl-NL',
      'pl': 'pl-PL',
      'tr': 'tr-TR'
    };
    
    // Configurazione
    this.recognition.continuous = true; // Ascolta continuamente
    this.recognition.interimResults = true; // Mostra risultati parziali
    this.recognition.maxAlternatives = 1; // Solo il risultato migliore
    
    // IMPORTANTE: Web Speech API riconosce la lingua parlata, non quella dell'interfaccia
    // Usa la lingua del browser come default, ma se l'utente parla in un'altra lingua,
    // il riconoscimento può comunque funzionare (dipende dal browser e dalle lingue installate)
    this.recognition.lang = langMap[langCode] || 'en-US'; // Default: lingua del browser
    
    // Event handlers
    this.recognition.onstart = () => {
      console.log('🎤 Riconoscimento vocale avviato');
      this.isListening = true;
      this.startTimer();
    };

    this.recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (this.onResultCallback) {
        this.onResultCallback(finalTranscript.trim(), interimTranscript.trim());
      }
    };

    this.recognition.onerror = (event) => {
      console.error('Errore riconoscimento vocale:', event.error);
      this.isListening = false;
      this.stopTimer();
      
      if (this.onErrorCallback) {
        this.onErrorCallback(event.error);
      }
    };

    this.recognition.onend = () => {
      console.log('🎤 Riconoscimento vocale terminato');
      this.isListening = false;
      this.stopTimer();
      
      if (this.onEndCallback) {
        this.onEndCallback();
      }
    };
  }

  startTimer() {
    this.timer = setTimeout(() => {
      this.stop();
    }, this.maxDuration);
  }

  stopTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Avvia il riconoscimento vocale
   * @param {Function} onResult - Callback chiamato con (finalTranscript, interimTranscript)
   * @param {Function} onError - Callback chiamato in caso di errore
   * @param {Function} onEnd - Callback chiamato quando il riconoscimento termina
   */
  start(onResult, onError, onEnd) {
    if (!this.isSupported) {
      if (onError) {
        onError('not-supported');
      }
      return false;
    }

    if (this.isListening) {
      console.warn('Riconoscimento vocale già in corso');
      return false;
    }

    this.onResultCallback = onResult;
    this.onErrorCallback = onError;
    this.onEndCallback = onEnd;

    try {
      this.recognition.start();
      return true;
    } catch (error) {
      console.error('Errore avvio riconoscimento:', error);
      if (onError) {
        onError('start-failed');
      }
      return false;
    }
  }

  /**
   * Ferma il riconoscimento vocale
   */
  stop() {
    if (!this.isSupported || !this.isListening) {
      return;
    }

    try {
      this.recognition.stop();
    } catch (error) {
      console.error('Errore stop riconoscimento:', error);
    }
  }

  /**
   * Verifica se il riconoscimento vocale è supportato
   */
  getSupported() {
    return this.isSupported;
  }

  /**
   * Verifica se sta ascoltando
   */
  getIsListening() {
    return this.isListening;
  }
}

// Esporta singleton
const speechRecognitionService = new SpeechRecognitionService();
export default speechRecognitionService;

